import React from 'react';
import { 
  BriefcaseIcon, 
  AcademicCapIcon,
  ChartBarIcon,
  CpuChipIcon,
  BanknotesIcon,
  UsersIcon
} from '@heroicons/react/20/solid';
import Header from './shared/Header';
import Footer from './Footer';
import SEO from './SEO';

const teamMembers = [
  {
    name: 'Qayyum Rajan, CFA',
    role: 'Chief Executive Officer',
    bio: `Qayyum ('Q') brings extensive experience across Canada's leading financial institutions and cutting-edge fintech ventures. He began his career at CIBC and RBC, working in portfolio management and investment funds, before advancing to senior roles at Sentry Investments and RBC Dominion Securities.

Q subsequently transitioned into the technology sector, co-founding a global fintech company focused on automating securities issuance using blockchain technology. He then joined the digital and emerging technologies consulting division at one of the Big 4, before founding and leading multiple successful ventures in quantitative analytics and alternative data.

Most recently, Q served as a senior executive at a prominent alternative data and quantitative research firm, where he developed sophisticated alpha generation strategies for institutional clients. Qayyum is a CFA Charterholder and was a Top 30 Under 30 award recipient. He also sits on the Fintech Advisory Committee for the BC Securities Commission (BCSC).`,
    icon: BriefcaseIcon,
    expertise: ['Quantitative Finance', 'Alternative Data', 'Fintech Innovation', 'Portfolio Management']
  },
  {
    name: 'Amar Morjaria',
    role: 'Managing Director - Model Portfolios',
    bio: `Amar brings over 20 years of experience helping asset managers, hedge funds, and advisors harness financial technology to drive smarter investment decisions. He has specialized expertise in bringing sophisticated quantitative solutions to market.

Amar started his career at Bloomberg in New Business and Relationship Management, generating significant revenues across the Nordics and London. He developed deep expertise in equities and comprehensive knowledge of the full trade lifecycle - from pre-trade analytics through execution and post-trade workflows.

He has held senior roles at leading financial technology firms including OTAS Technologies (a Liquidnet company) and Trendrating, where he focused on delivering advanced data analytics and model portfolios to institutional clients worldwide. His passion lies in building products that make a genuine difference to how investment professionals work and helping them scale their practices.

Amar holds a BA (Hons) in Economics and Financial Services.`,
    icon: BanknotesIcon,
    expertise: ['Institutional Sales', 'Model Portfolios', 'Trade Lifecycle', 'Client Relationship Management']
  },
  {
    name: 'Julie Craig',
    role: 'Chief Marketing Officer',
    bio: `Julie brings over 15+ years of comprehensive marketing expertise across B2B and B2C environments, with deep specialization in fintech and financial services. Her experience spans the complete marketing spectrum from strategy development through execution and analysis.

Julie has held senior marketing leadership roles at cutting-edge fintech companies including AlphaSense (now valued at $4B), Compliance.ai, Hummingbird, and OANDA's Solutions for Business division. She has consistently driven growth for AI-powered platforms serving banks, asset managers, hedge funds, and investment advisors.

Her expertise encompasses demand generation, product marketing, account-based marketing, marketing automation, and sales enablement. Julie excels at translating complex financial technology into compelling value propositions that resonate with sophisticated institutional clients.`,
    icon: ChartBarIcon,
    expertise: ['B2B Marketing Strategy', 'Fintech Marketing', 'Demand Generation', 'Product Positioning']
  },
  {
    name: 'Natasha Brui',
    role: 'Data Operations Manager',
    bio: `Natasha serves as our Data Operations Manager with 7+ years of experience as an IT professional with specialized expertise in Finance, Cybersecurity, and Data Analytics within progressive alternative data environments.

She has extensive experience monitoring and managing complex financial data platforms, ensuring data integrity and operational excellence for institutional clients. Natasha has worked with leading alternative data firms, helping institutional investors, hedge funds, and asset managers leverage unique datasets for alpha generation.

Her background combines technical proficiency with deep understanding of financial markets, making her uniquely qualified to support our advisor clients in maximizing the value of our platform and data offerings. She excels at translating complex technical capabilities into practical solutions for investment professionals.

Natasha's experience includes working with platforms that serve thousands of financial professionals, from hedge fund analysts to independent investment advisors, providing them with critical market insights and competitive intelligence.`,
    icon: UsersIcon,
    expertise: ['Data Operations', 'Client Success', 'Financial Technology', 'Alternative Data Platforms']
  }
];

export default function TeamPage() {
  return (
    <>
      <SEO 
        title="Our Team"
        description="Meet the SharpeMetrix leadership team - experienced professionals at the intersection of finance, technology, and alternative data, dedicated to empowering financial advisors with institutional-grade investment solutions."
        keywords="SharpeMetrix team, fintech leadership, quantitative finance, alternative data experts, financial technology, investment management"
        url="/team"
      />
      <Header />

      {/* Hero Section */}
      <div className="relative isolate overflow-hidden bg-bg-100 pt-14">
        <div aria-hidden="true" className="absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80">
          <div
            className="relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#2E5BFF] to-[#00D4FF] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]"
            style={{
              clipPath:
                'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)',
            }}
          />
        </div>
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-text-100 sm:text-6xl">
              Meet Our Team
            </h1>
            <p className="mt-6 text-lg leading-8 text-text-200">
              Experienced professionals at the intersection of finance, technology, and alternative data, 
              dedicated to empowering financial advisors with institutional-grade investment solutions.
            </p>
          </div>
        </div>
      </div>

      {/* Team Members Section */}
      <div className="bg-bg-200 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">
              Leadership Team
            </h2>
            <p className="mt-4 text-lg leading-8 text-text-200">
              Our team combines decades of experience in quantitative finance, alternative data, 
              and financial technology to deliver sophisticated solutions for investment professionals.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-bg-100 rounded-2xl p-8 ring-1 ring-bg-300/10 shadow-lg">
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    <div className="rounded-lg bg-primary-100/10 p-3">
                      <member.icon className="h-8 w-8 text-primary-100" aria-hidden="true" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-text-100 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-primary-100 font-medium mb-4">
                      {member.role}
                    </p>
                    <div className="prose prose-sm text-text-200 mb-6">
                      {member.bio.split('\n\n').map((paragraph, pIndex) => (
                        <p key={pIndex} className="mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {member.expertise.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="inline-flex items-center rounded-md bg-primary-100/10 px-2 py-1 text-xs font-medium text-primary-100 ring-1 ring-inset ring-primary-100/20"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Values/Mission Section */}
      <div className="bg-bg-100 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-3xl font-bold tracking-tight text-text-100 sm:text-4xl">
              Our Mission
            </h2>
            <p className="mt-6 text-lg leading-8 text-text-200">
              We're dedicated to democratizing institutional-grade quantitative strategies and alternative data insights, 
              empowering financial advisors to deliver differentiated, data-driven investment solutions to their clients.
            </p>
          </div>
          
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:max-w-none lg:grid-cols-3">
            <div className="flex flex-col items-center text-center">
              <div className="rounded-md bg-primary-100/10 p-3 mb-4">
                <CpuChipIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-text-100 mb-2">Innovation</h3>
              <p className="text-text-200">
                Continuously advancing the frontier of alternative data and quantitative modeling 
                to uncover unique alpha opportunities.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="rounded-md bg-primary-100/10 p-3 mb-4">
                <AcademicCapIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-text-100 mb-2">Excellence</h3>
              <p className="text-text-200">
                Maintaining the highest standards of research rigor and systematic approach 
                in everything we deliver to our clients.
              </p>
            </div>
            
            <div className="flex flex-col items-center text-center">
              <div className="rounded-md bg-primary-100/10 p-3 mb-4">
                <UsersIcon className="h-8 w-8 text-primary-100" aria-hidden="true" />
              </div>
              <h3 className="text-lg font-semibold text-text-100 mb-2">Partnership</h3>
              <p className="text-text-200">
                Building lasting relationships with financial advisors by truly understanding 
                their challenges and delivering solutions that matter.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
} 